/**
 * CAOS-610 — declaring that the PAGE is waiting, and retracting it when it is not.
 *
 * The host's own boot is the case the ticket describes and the first one to use it. A visitor
 * arrives at a URL, `main.ts` asks the kernel which surface to paint, and until that answer comes
 * back the mount is empty. Nobody clicked a control, so there is no button to wear the wait; the
 * page has not decided what it is yet, so there is no layout whose shape a placeholder could stand
 * in for. The thing that is busy is the page.
 *
 * ── WHY RETRACTING IS A SEPARATE STEP FROM REMOVING ──────────────────────────────────────────
 * The pending element's line of copy lives in a `role="status"` live region. Text arriving there is
 * announced; text LEAVING it is what retracts the announcement. Taking the whole node out of the
 * document does neither — the region simply stops existing, and a reader who was told the page was
 * loading is never told anything else.
 *
 * So {@link declarePagePending} hands back a resolve that clears `pending` FIRST, while the element
 * is still in the document and its live region is still being observed, and only then removes it.
 * It matters that the caller runs that resolve BEFORE painting the answer, because painting clears
 * the mount — `SurfaceHost.render` calls `replaceChildren` — and a resolve running after that is
 * operating on a detached node nothing is listening to.
 *
 * Calling it twice is safe, which is what lets the boot retract at the right moment on the happy
 * path AND keep a `finally` for the path where a read throws. Worth being exact about why: a second
 * call would be inert anyway — removing an attribute from a detached node and removing a node that
 * is already gone are both no-ops — so the `resolved` flag does not CREATE that property, it states
 * it. Mutation-tested: taking the flag out reds nothing, which is the honest answer rather than a
 * test invented for behaviour that is not there.
 */

/** What the boot says it is waiting on. Names the thing, not the fact that something is happening. */
export const BOOT_PENDING_LABEL = 'Starting your workspace…';

/**
 * Declare that this page is waiting, and hand back the retraction.
 *
 * Appends the pending state to `mount` and returns a function that clears it. The returned function
 * is safe to call more than once.
 */
export function declarePagePending(mount: HTMLElement, label: string = BOOT_PENDING_LABEL): () => void {
  const el = document.createElement('caos-page-pending');
  el.setAttribute('label', label);
  el.setAttribute('pending', '');
  mount.append(el);

  let resolved = false;
  return () => {
    if (resolved) return;
    resolved = true;
    // Order is the whole point — see the header. Clear the state while it is still being observed,
    // then take the node away.
    el.removeAttribute('pending');
    el.remove();
  };
}
